import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, ApiClientError } from "@/lib/api";
import type { AgentFlowAccountDTO, AgentFlowDTO } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { AssistedFlowBuilder } from "./AssistedFlowBuilder";

type AccountState =
  | { status: "loading" }
  | { status: "inactive" }
  | { status: "ready"; account: AgentFlowAccountDTO }
  | { status: "error"; message: string };

type FlowsState =
  | { status: "loading" }
  | { status: "ready"; flows: AgentFlowDTO[] }
  | { status: "error"; message: string };

/**
 * AgentFlow index page — M0 skeleton.
 *
 * Two surfaces:
 *   1. "Activate AgentFlow" CTA when the tenant has no AgentFlowAccount.
 *   2. A trivial flow list once they're activated. Clicking a flow opens
 *      a placeholder ("Coming in M1") — the real editor lands in M1.
 *
 * No fake data. Empty states are real states.
 */
export function AgentFlowIndex() {
  const [account, setAccount] = useState<AccountState>({ status: "loading" });
  const [flows, setFlows] = useState<FlowsState>({ status: "loading" });
  const [activating, setActivating] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void api.agentflow
      .getAccount()
      .then((acc) => {
        if (cancelled) return;
        setAccount({ status: "ready", account: acc });
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiClientError && err.code === "agentflow_account_not_found") {
          setAccount({ status: "inactive" });
          setFlows({ status: "ready", flows: [] });
          return;
        }
        setAccount({
          status: "error",
          message: err instanceof ApiClientError ? err.message : "Failed to load",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (account.status !== "ready") return;
    let cancelled = false;
    void api.agentflow
      .listFlows()
      .then((res) => {
        if (!cancelled) setFlows({ status: "ready", flows: res.flows });
      })
      .catch((err) => {
        if (!cancelled) {
          setFlows({
            status: "error",
            message: err instanceof ApiClientError ? err.message : "Failed to load",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [account.status, refreshKey]);

  async function handleActivate() {
    setActivating(true);
    try {
      const acc = await api.agentflow.activate();
      setAccount({ status: "ready", account: acc });
    } catch (err) {
      setAccount({
        status: "error",
        message: err instanceof ApiClientError ? err.message : "Failed to activate",
      });
    } finally {
      setActivating(false);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <p className="text-[11.5px] font-semibold uppercase tracking-[0.16em] text-brand-600 dark:text-brand-400">
          Beta · M0 preview
        </p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
          AgentFlow
        </h1>
        <p className="mt-1 max-w-2xl text-[13.5px] text-zinc-600 dark:text-zinc-400">
          Conversational flows that handle inbound SMS through your Twilio numbers. The visual
          canvas lands in M1 — for now you can activate the module, define flows by system prompt,
          and bind them to numbers via the API.
        </p>
      </header>

      {account.status === "loading" && <SkeletonCard />}

      {account.status === "error" && <ErrorBanner message={account.message} />}

      {account.status === "inactive" && (
        <ActivateCard onActivate={handleActivate} loading={activating} />
      )}

      {account.status === "ready" && (
        <>
          <AccountSummary account={account.account} />
          <FlowsSection state={flows} onChanged={() => setRefreshKey((k) => k + 1)} />
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

function ActivateCard({ onActivate, loading }: { onActivate: () => void; loading: boolean }) {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 dark:border-zinc-800 dark:bg-zinc-900/30">
      <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
        Activate AgentFlow
      </h2>
      <p className="mt-1.5 max-w-xl text-[13px] text-zinc-600 dark:text-zinc-400">
        We&rsquo;ll create an AgentFlow account for this tenant with sensible defaults — Anthropic
        Claude Sonnet, platform-managed keys, no flows yet. You can change the provider and bring
        your own key later.
      </p>
      <div className="mt-4">
        <Button onClick={onActivate} disabled={loading}>
          {loading ? "Activating…" : "Activate AgentFlow"}
        </Button>
      </div>
    </div>
  );
}

function AccountSummary({ account }: { account: AgentFlowAccountDTO }) {
  return (
    <section className="rounded-xl border border-zinc-200 bg-white p-5 shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Account</p>
          <h2 className="mt-0.5 text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
            Status:{" "}
            <span className="font-normal text-zinc-700 dark:text-zinc-300">{account.status}</span>
          </h2>
        </div>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-[12.5px] sm:grid-cols-3">
          <Stat label="Provider" value={account.llm.provider} />
          <Stat label="Model" value={account.llm.model} />
          <Stat label="BYOK" value={account.llm.byok ? "On" : "Off"} />
        </dl>
      </div>
    </section>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-zinc-500">{label}</dt>
      <dd className="font-mono text-zinc-800 dark:text-zinc-200">{value}</dd>
    </div>
  );
}

function FlowsSection({ state, onChanged }: { state: FlowsState; onChanged: () => void }) {
  return (
    <section>
      <div className="mb-3 flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">Flows</h2>
          <p className="mt-0.5 text-[12.5px] text-zinc-600 dark:text-zinc-400">
            Open a flow to edit it on the canvas, clone a template, or start from scratch.
          </p>
        </div>
        <NewFlowButton />
      </div>

      {state.status === "loading" && <SkeletonCard />}
      {state.status === "error" && <ErrorBanner message={state.message} />}
      {state.status === "ready" && state.flows.length === 0 && <EmptyFlows />}
      {state.status === "ready" && state.flows.length > 0 && (
        <FlowList flows={state.flows} onChanged={onChanged} />
      )}
    </section>
  );
}

/**
 * Single CTA that opens a unified "Create new flow" dialog with two
 * paths: clone a template (recommended) or start blank (name only).
 * Replaces the old template-only dropdown.
 */
function NewFlowButton() {
  const navigate = useNavigate();
  const [view, setView] = useState<"closed" | "dialog" | "ai">("closed");
  const goToFlow = (id: string) => navigate(`/agentflow/flows/${id}`);
  return (
    <>
      <Button onClick={() => setView("dialog")}>New flow</Button>
      {view === "dialog" && (
        <NewFlowDialog
          onClose={() => setView("closed")}
          onCreated={goToFlow}
          onChooseAi={() => setView("ai")}
        />
      )}
      {view === "ai" && (
        <AssistedFlowBuilder onClose={() => setView("closed")} onCreated={goToFlow} />
      )}
    </>
  );
}

interface TemplateOption {
  id: string;
  name: string;
  description: string;
}

type Loading<T> =
  | { status: "loading" }
  | { status: "ready"; data: T }
  | { status: "error"; message: string };

function NewFlowDialog({
  onClose,
  onCreated,
  onChooseAi,
}: {
  onClose: () => void;
  onCreated: (flowId: string) => void;
  onChooseAi: () => void;
}) {
  const [templates, setTemplates] = useState<Loading<TemplateOption[]>>({ status: "loading" });
  const [blankName, setBlankName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const blankInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.agentflow
      .listTemplates()
      .then((res) => {
        if (cancelled) return;
        setTemplates({
          status: "ready",
          data: res.templates.map((t) => ({
            id: t.id,
            name: t.name,
            description: t.description,
          })),
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setTemplates({
          status: "error",
          message: e instanceof ApiClientError ? e.message : "Failed to load templates",
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Esc closes (when not mid-create) + backdrop close.
  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [busy, onClose]);

  function onBackdropMouseDown(e: React.MouseEvent) {
    if (e.target === e.currentTarget && !busy) onClose();
  }

  async function pickTemplate(template: TemplateOption) {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      const flow = await api.agentflow.cloneTemplate({
        templateId: template.id,
        name: `${template.name} — Draft`,
      });
      onCreated(flow.id);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to create flow");
      setBusy(false);
    }
  }

  async function createBlank() {
    const name = blankName.trim();
    if (!name || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const flow = await api.agentflow.createFlow({ name });
      onCreated(flow.id);
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to create flow");
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="new-flow-title"
      onMouseDown={onBackdropMouseDown}
      className="fixed inset-0 z-30 flex items-start justify-center bg-zinc-950/40 p-4 sm:items-center"
    >
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
        <header className="flex items-start justify-between gap-3 border-b border-zinc-200 px-5 py-3.5 dark:border-zinc-800">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-600 dark:text-brand-400">
              AgentFlow
            </p>
            <h2
              id="new-flow-title"
              className="mt-0.5 text-[15px] font-semibold text-zinc-900 dark:text-zinc-100"
            >
              Create new flow
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="-mr-1 inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:hover:bg-zinc-800 dark:hover:text-zinc-100"
          >
            ✕
          </button>
        </header>

        <div className="space-y-5 overflow-y-auto px-5 py-4">
          {/* Templates */}
          <section aria-labelledby="templates-heading">
            <h3
              id="templates-heading"
              className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
            >
              Start from a template
            </h3>
            {templates.status === "loading" && (
              <div className="h-20 animate-pulse rounded-md bg-zinc-100 dark:bg-zinc-800/40" />
            )}
            {templates.status === "error" && (
              <p
                role="alert"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
              >
                {templates.message}
              </p>
            )}
            {templates.status === "ready" && templates.data.length === 0 && (
              <p className="rounded-md border border-dashed border-zinc-200 bg-zinc-50 px-3 py-3 text-center text-[12.5px] text-zinc-500 dark:border-zinc-800 dark:bg-zinc-900/30">
                No templates available.
              </p>
            )}
            {templates.status === "ready" && templates.data.length > 0 && (
              <ul className="space-y-2">
                {templates.data.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      onClick={() => void pickTemplate(t)}
                      disabled={busy}
                      className="group flex w-full items-start gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/5"
                    >
                      <div className="flex-1">
                        <p className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
                          {t.name}
                        </p>
                        <p className="mt-0.5 text-[12px] text-zinc-500">{t.description}</p>
                      </div>
                      <span className="mt-0.5 shrink-0 text-[11.5px] font-medium text-brand-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-brand-300">
                        Use →
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Divider */}
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-400">or</span>
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
          </div>

          {/* Describe it (AI) */}
          <section aria-labelledby="ai-heading">
            <h3
              id="ai-heading"
              className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
            >
              Describe it · AI builds the draft
            </h3>
            <button
              type="button"
              onClick={onChooseAi}
              disabled={busy}
              className="group flex w-full items-start gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2.5 text-left transition-colors hover:border-brand-300 hover:bg-brand-50/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-800 dark:bg-zinc-900/40 dark:hover:border-brand-500/40 dark:hover:bg-brand-500/5"
            >
              <div className="flex-1">
                <p className="text-[13.5px] font-medium text-zinc-900 dark:text-zinc-100">
                  Build with AI
                </p>
                <p className="mt-0.5 text-[12px] text-zinc-500">
                  Describe your business and we&apos;ll generate intents, actions, and guardrails —
                  with a few quick questions if needed — for you to review.
                </p>
              </div>
              <span className="mt-0.5 shrink-0 text-[11.5px] font-medium text-brand-600 opacity-0 transition-opacity group-hover:opacity-100 dark:text-brand-300">
                Start →
              </span>
            </button>
          </section>

          {/* Divider */}
          <div className="flex items-center gap-3" aria-hidden="true">
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
            <span className="text-[11px] uppercase tracking-wider text-zinc-400">or</span>
            <span className="h-px flex-1 bg-zinc-200 dark:bg-zinc-800" />
          </div>

          {/* Blank */}
          <section aria-labelledby="blank-heading">
            <h3
              id="blank-heading"
              className="mb-2 text-[11px] font-medium uppercase tracking-wider text-zinc-500"
            >
              Start from scratch
            </h3>
            <p className="mb-2 text-[12px] text-zinc-500">
              An empty draft with a default persona and greeting. Add intents, actions, and
              guardrails on the canvas.
            </p>
            <div className="flex gap-2">
              <input
                ref={blankInputRef}
                value={blankName}
                onChange={(e) => setBlankName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && blankName.trim() && !busy) {
                    e.preventDefault();
                    void createBlank();
                  }
                }}
                placeholder="Name your flow…"
                disabled={busy}
                aria-label="Flow name"
                className="h-10 flex-1 rounded-md border border-zinc-200 bg-white px-3 text-[13.5px] text-zinc-900 placeholder:text-zinc-400 focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/30 disabled:bg-zinc-50 disabled:text-zinc-400 dark:border-zinc-800 dark:bg-zinc-950/40 dark:text-zinc-100 dark:placeholder:text-zinc-600 dark:disabled:bg-zinc-900/30"
              />
              <Button
                onClick={() => void createBlank()}
                disabled={busy || blankName.trim().length === 0}
              >
                {busy ? "Creating…" : "Create blank"}
              </Button>
            </div>
            <p className="mt-1 text-[10.5px] text-zinc-500">↵ to create</p>
          </section>

          {err && (
            <p
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
            >
              {err}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FlowList({ flows, onChanged }: { flows: AgentFlowDTO[]; onChanged: () => void }) {
  const [toDelete, setToDelete] = useState<AgentFlowDTO | null>(null);
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-card-light dark:border-zinc-800/80 dark:bg-zinc-900/40 dark:shadow-none">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:bg-zinc-900/60">
          <tr>
            <th className="px-4 py-2.5">Name</th>
            <th className="px-4 py-2.5">Status</th>
            <th className="px-4 py-2.5">Published version</th>
            <th className="px-4 py-2.5 text-right">Updated</th>
            <th className="px-4 py-2.5 text-right">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800/80">
          {flows.map((f) => (
            <tr key={f.id} className="transition hover:bg-zinc-50 dark:hover:bg-zinc-900/60">
              <td className="px-4 py-3 align-middle">
                <Link
                  to={`/agentflow/flows/${f.id}`}
                  className="font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300 dark:hover:text-brand-200"
                >
                  {f.name}
                </Link>
              </td>
              <td className="px-4 py-3 align-middle">
                <span
                  className={
                    "inline-flex rounded px-1.5 py-0.5 text-[11px] font-medium capitalize " +
                    (f.status === "published"
                      ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
                      : "bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300")
                  }
                >
                  {f.status}
                </span>
              </td>
              <td className="px-4 py-3 align-middle">
                {/* version is bumped on publish (0 = never published). It's the
                    version serving live traffic, even when the draft has newer edits. */}
                {f.version >= 1 ? (
                  <span
                    className="inline-flex items-center gap-1 font-mono text-[12.5px] text-emerald-700 dark:text-emerald-300"
                    title="The published version currently serving live traffic."
                  >
                    Live · v{f.version}
                  </span>
                ) : (
                  <span className="text-[12.5px] text-zinc-400" title="Not published yet">
                    —
                  </span>
                )}
              </td>
              <td className="px-4 py-3 align-middle text-right text-[12.5px] text-zinc-500">
                {new Date(f.updatedAt).toLocaleString()}
              </td>
              <td className="px-4 py-3 align-middle text-right">
                <button
                  type="button"
                  onClick={() => setToDelete(f)}
                  aria-label={`Delete ${f.name}`}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                >
                  <svg viewBox="0 0 20 20" fill="none" className="h-4 w-4" aria-hidden="true">
                    <path
                      d="M7.5 8.5v6m5-6v6M3.5 5.5h13m-1.5 0-.7 10a1.5 1.5 0 0 1-1.5 1.4H7.7a1.5 1.5 0 0 1-1.5-1.4l-.7-10m3-2.5h4a1 1 0 0 1 1 1v1.5H7V4a1 1 0 0 1 1-1Z"
                      stroke="currentColor"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {toDelete && (
        <DeleteFlowDialog
          flow={toDelete}
          onClose={() => setToDelete(null)}
          onDeleted={() => {
            setToDelete(null);
            onChanged();
          }}
        />
      )}
    </div>
  );
}

function DeleteFlowDialog({
  flow,
  onClose,
  onDeleted,
}: {
  flow: AgentFlowDTO;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === "Escape" && !busy) onClose();
    }
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [busy, onClose]);

  async function confirmDelete() {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await api.agentflow.deleteFlow(flow.id);
      onDeleted();
    } catch (e) {
      setErr(e instanceof ApiClientError ? e.message : "Failed to delete flow");
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-flow-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      className="fixed inset-0 z-30 flex items-center justify-center bg-zinc-950/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg border border-zinc-200 bg-white p-5 shadow-xl dark:border-zinc-800 dark:bg-zinc-900">
        <h2
          id="delete-flow-title"
          className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100"
        >
          Delete “{flow.name}”?
        </h2>
        <p className="mt-2 text-[13px] text-zinc-600 dark:text-zinc-400">
          This permanently deletes the flow, all its published versions, its conversation history,
          and any channel/phone-number bindings. This can&apos;t be undone.
        </p>
        {err && (
          <p
            role="alert"
            className="mt-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[12.5px] text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
          >
            {err}
          </p>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="danger" onClick={() => void confirmDelete()} disabled={busy}>
            {busy ? "Deleting…" : "Delete flow"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EmptyFlows() {
  return (
    <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-8 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
      <h3 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">No flows yet</h3>
      <p className="mx-auto mt-1 max-w-md text-[13px] text-zinc-600 dark:text-zinc-400">
        Hit <span className="font-medium text-zinc-800 dark:text-zinc-200">New flow</span> above to
        clone a template or start with a blank canvas.
      </p>
    </div>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-5 dark:border-zinc-800/80 dark:bg-zinc-900/40">
      <div className="h-3 w-24 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
      <div className="mt-3 h-3 w-3/4 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
      <div className="mt-2 h-3 w-1/2 animate-pulse rounded bg-zinc-200 dark:bg-zinc-800/70" />
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
      {message}
    </p>
  );
}
